import { createFeatureSelector, createSelector } from '@ngrx/store';
import { AptCommodityType } from '../../common/enums/apttus/apt-commodity-typeof-sale';
import { AptProductType } from '../../common/enums/apttus/apt-product-type';
import { D365CustomerSegment } from '../../common/enums/d365/d365-customer-segment';
import {
    cleanObj,
    isActivePackageProduct,
    isNotDiscountItem,
    isVisibleNotTechProduct,
} from '../../common/functions/misc.functions';
import { getNumberOrNull } from '../../common/functions/remap.functions';
import {
    containsProductGas,
    containsProductPower,
    containsProductSmartHome,
    flowTypeUtil,
    isCommodityFamily,
} from '../../common/functions/verifications.functions';
import { MacroFlowType } from '../models/flow-type';
import { OrderEntryState_v2, Product } from '../models/order-entry-state_v2';
import { UserState } from '../models/user-state';
import { TargetProductIndex } from '../models/utility.types';

const v2OrderEntryState = createFeatureSelector<OrderEntryState_v2>('orderEntry');

export const isCommodityProduct =
    (commodityType?: AptCommodityType) =>
    ({ powerOrGas, family }: Pick<Product, 'powerOrGas' | 'family'>) =>
        isCommodityFamily(family) && ((!commodityType && !!powerOrGas) || commodityType === powerOrGas);

export const isAssetCommodityProduct =
    (commodityType?: AptCommodityType) =>
    ({
        podPdr,
        assetId,
        powerOrGas,
        sourceCustomer,
        family,
    }: Pick<Product, 'powerOrGas' | 'family' | 'podPdr' | 'assetId' | 'sourceCustomer'>): boolean =>
        !!podPdr &&
        (!!assetId || !!sourceCustomer?.assetId) &&
        isCommodityProduct(commodityType)({ powerOrGas, family });

export const isPendingAssetCommodityProduct = (commodityType?: AptCommodityType) => (product: Product) =>
    isAssetCommodityProduct(commodityType)(product) && !product.lineItemId;

export const isPowerProduct = (product: Product) =>
    product?.powerOrGas === AptCommodityType.Power || containsProductPower(product?.productType);
export const isGasProduct = (product: Product) =>
    product?.powerOrGas === AptCommodityType.Gas || containsProductGas(product?.productType);

export const isActiveVisibleMainProduct = (product: Product) =>
    isActivePackageProduct(product) && isNotDiscountItem(product);

export const isVisibleProduct = (product: Product) => isVisibleNotTechProduct(product) && isNotDiscountItem(product);

const PRODUCT_FILTER_MAP: {
    [key in TargetProductIndex]: (product: Product, index?: number) => boolean;
} = {
    [AptCommodityType.Power]: isPowerProduct,
    [AptCommodityType.Gas]: isGasProduct,
    ALL: (product) => true,
    SMARTHOME: (product) => containsProductSmartHome([product?.productType]),
};

export function* productFieldSelectorGenerator<T>({
    state,
    productSelector,
    productIdx,
}: {
    state: Pick<OrderEntryState_v2, 'products' | 'flowType'>;
    productSelector: (product: Product) => T;
    productIdx?: TargetProductIndex;
}): Generator<T> {
    const queryParamProductIdx = getNumberOrNull(new URLSearchParams(location.search).get('itemId'));

    const targetIndex = [
        // valore in input numerico o ALL
        getNumberOrNull(productIdx) || (<TargetProductIndex[]>['ALL', 'SMARTHOME']).includes(productIdx)
            ? productIdx
            : null,
        // Query Param
        typeof queryParamProductIdx === 'number' && queryParamProductIdx,
        // Valore in input POWER/GAS
        productIdx,
        // Default
        'ALL',
    ].find(
        (value) =>
            (typeof value === 'number' && value >= 0) ||
            Object.values(AptCommodityType).includes(value as AptCommodityType) ||
            ['ALL', 'SMARTHOME'].includes('' + value),
    );

    const productFilter =
        typeof targetIndex === 'number'
            ? // Se idx !== targetIndex controllo anche se idx di prodotto è uguale a targetIndex, perché con tanti prodotti tecnici non salvati nello state c'è una discrepanza degli indici dei prodotti
              (product: Product, idx: number) => idx === targetIndex || product.idx === targetIndex
            : PRODUCT_FILTER_MAP[targetIndex];

    // Filtro la lista prodotti per avere solo quelli visibili
    const products = (state?.products || []).filter(
        flowTypeUtil(state.flowType).inMacroFlowTypes(MacroFlowType.Cessazione, MacroFlowType.BroadBandCessazioni)
            ? isVisibleProduct
            : isActiveVisibleMainProduct,
    );

    for (let idx = 0; idx < state?.products?.length; idx++) {
        // aggiunto controllo sul productIdx passato in ingresso, perché idx valuta solo i prodotti salvati nello state e di conseguenza non verranno mai presi in considerazione i productIdx > idx
        const product = products.find((p) => p.idx === idx || p.idx === getNumberOrNull(productIdx));
        if (product && productFilter(product, idx)) {
            const foundProduct = productSelector(product);
            if (foundProduct) {
                yield foundProduct;
            }
        }
    }
}

export function productsFieldRetrieverGenerator(products: Product[]) {
    return <T>(productFieldSelector: (product: Product) => T): T => {
        const targetProduct = (products || []).find(productFieldSelector);
        return targetProduct && productFieldSelector(targetProduct);
    };
}

export function wrapperSelectProductByIndex<T>(productSelector: (product: Product) => T) {
    return (productIdx?: TargetProductIndex) =>
        createSelector(
            v2OrderEntryState,
            (state) =>
                productFieldSelectorGenerator({
                    state,
                    productSelector,
                    productIdx,
                }).next().value as T,
        );
}

export const filterCommodityProducts = (product: Product) =>
    Object.values(AptCommodityType).includes(product?.powerOrGas) && isActiveVisibleMainProduct(product);

export const filterMaintenanceProducts = (product: Product) =>
    product?.productType === AptProductType.Manutenzione && isActiveVisibleMainProduct(product);

export function getContactNameFromUserState(state: UserState): string {
    const contact = cleanObj(state?.contact || state?.lead);
    if (contact && Object.values(contact).length > 0) {
        return contact.egl_customersegmentcode === D365CustomerSegment.Microbusiness
            ? `${contact.name}  (${contact.egl_vatcode})`.trim()
            : `${contact.firstname}  ${contact.lastname}  (${contact.egl_taxcode})`.trim();
    }
    return null;
}
